/**
 * Pestaña En vivo: marcador grande + feed de eventos entrando por SSE.
 *
 * Protocolo de reconexión (el que documenta el API):
 *   1. abre el stream → llega `snapshot` con `lastSeq`;
 *   2. pide `GET /events` para cubrir el hueco (acá se pide el log completo:
 *      es una sola llamada y deja el marcador correcto aunque el portal se
 *      haya abierto a mitad del partido);
 *   3. sigue consumiendo el stream.
 * La reconexión con backoff la maneja `LiveSubscription`.
 *
 * OJO con los permisos: el stream pide `matches:read` pero el catch-up del
 * paso 2 pide `events:read`, que el rol `viewer` no tiene. Ese 403 NO tumba la
 * pestaña: el vivo sigue andando con lo que entra por el stream y sólo se
 * avisa que el partido empieza a contarse desde que uno se conectó.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { ScoutEvent } from '@scout/core';
import { Card, Empty } from '../../components/States';
import { useT } from '../../i18n/use-t';
import { isForbidden, type ApiClient } from '../../lib/api-client';
import { describeEvent } from '../../lib/describe';
import { LiveSubscription, liveStatusLabel, type LiveStatus } from '../../lib/live';
import { normalizeLog, readMatchMeta, readSetResults, setsWon } from '../../lib/log';

const FEED_SIZE = 40;

function statusChipClass(status: LiveStatus): string {
  switch (status) {
    case 'open':
      return 'chip ok';
    case 'connecting':
    case 'reconnecting':
      return 'chip warn';
    case 'error':
      return 'chip bad';
    default:
      return 'chip muted';
  }
}

export function LiveTab({
  client,
  matchId,
}: {
  client: ApiClient;
  matchId: string;
}): JSX.Element {
  const [events, setEvents] = useState<ScoutEvent[]>([]);
  const [status, setStatus] = useState<LiveStatus>('idle');
  const [detail, setDetail] = useState<string | null>(null);
  /** El rol no puede leer el log: hay stream, no hay catch-up ni feed histórico. */
  const [noLog, setNoLog] = useState(false);
  const seen = useRef<Set<string>>(new Set());
  const t = useT();

  const merge = useCallback((incoming: readonly ScoutEvent[]) => {
    const fresh = incoming.filter((e) => !seen.current.has(e.id));
    if (fresh.length === 0) return;
    for (const e of fresh) seen.current.add(e.id);
    setEvents((prev) => [...prev, ...fresh].sort((a, b) => a.seq - b.seq));
  }, []);

  useEffect(() => {
    let alive = true;
    seen.current = new Set();
    setEvents([]);
    setDetail(null);
    setNoLog(false);

    const catchUp = (): void => {
      client
        .getEvents(matchId)
        .then((page) => {
          if (alive) merge(page.events);
        })
        .catch((err: unknown) => {
          if (!alive) return;
          // 403: no es una falla de conexión, es el rol. Se anota y se sigue:
          // el stream ya está abierto y el marcador se arma con lo que entre.
          if (isForbidden(err)) {
            setNoLog(true);
            return;
          }
          setDetail(err instanceof Error ? err.message : String(err));
        });
    };

    const subscription = new LiveSubscription({
      url: client.liveUrl(matchId),
      headers: client.authHeaders(),
      onStatus: (next, why) => {
        if (!alive) return;
        setStatus(next);
        if (why) setDetail(why);
      },
      // El snapshot llega al conectar Y en cada reconexión: siempre se cubre el hueco.
      onSnapshot: () => catchUp(),
      onEvent: (event) => {
        if (alive) merge([event]);
      },
    });
    subscription.start();

    return () => {
      alive = false;
      subscription.stop();
    };
  }, [client, matchId, merge]);

  const view = useMemo(() => {
    const normalized = normalizeLog(events);
    const meta = readMatchMeta(normalized, matchId);
    const results = readSetResults(normalized);
    const won = setsWon(results);

    // Marcador del set en curso: se recuenta desde los `point` del último set.
    let currentSet = 1;
    for (const { set } of normalized) currentSet = Math.max(currentSet, set);
    const closed = new Set(results.map((r) => r.setNumber));
    const live = { home: 0, away: 0 };
    for (const { event, set } of normalized) {
      if (event.type !== 'point' || set !== currentSet) continue;
      live[event.payload.wonBy] += 1;
    }

    return {
      meta,
      results,
      won,
      currentSet,
      live,
      finished: closed.has(currentSet),
      feed: normalized.slice(-FEED_SIZE).reverse(),
    };
  }, [events, matchId]);

  const names = {
    home: view.meta.teams.home.name,
    away: view.meta.teams.away.name,
    players: { home: view.meta.teams.home.names, away: view.meta.teams.away.names },
  };

  return (
    <div>
      <Card
        actions={
          <span className={statusChipClass(status)} title={detail ?? undefined}>
            <span className="dot" /> {liveStatusLabel(status, t)}
          </span>
        }
      >
        <div className="scoreboard">
          <div className="team-score home">
            <span className="side-name home">{names.home}</span>
            <span className="score">{view.live.home}</span>
          </div>
          <div className="middle">
            <div>{t('live.sets', { home: view.won.home, away: view.won.away })}</div>
            <div className="set-number">{t('common.set', { set: view.currentSet })}</div>
            {view.finished ? <div className="hint">{t('live.setClosed')}</div> : null}
          </div>
          <div className="team-score away">
            <span className="side-name away">{names.away}</span>
            <span className="score">{view.live.away}</span>
          </div>
        </div>
        {view.results.length > 0 ? (
          <div className="set-pills" style={{ justifyContent: 'center' }}>
            {view.results.map((set) => (
              <span key={set.setNumber} className="set-pill">
                S{set.setNumber} <b>{set.home}</b>–<b>{set.away}</b>
              </span>
            ))}
          </div>
        ) : null}
        {/* Si el feed ya tiene algo, el aviso va acá (abajo lo tapa la lista). */}
        {noLog && view.feed.length > 0 ? <p className="hint">{t('access.noEventsLive')}</p> : null}
        {status === 'error' && detail ? <p className="hint">{detail}</p> : null}
      </Card>

      <Card title={t('live.lastEvents')}>
        {noLog && view.feed.length === 0 ? (
          <Empty>{t('access.noEventsLive')}</Empty>
        ) : view.feed.length === 0 ? (
          <Empty>{t('live.empty')}</Empty>
        ) : (
          <ul className="feed">
            {view.feed.map(({ event }) => (
              <li key={event.id}>
                <span className="seq">#{event.seq}</span>
                <span className="desc">{describeEvent(event, names, t)}</span>
              </li>
            ))}
          </ul>
        )}
      </Card>
    </div>
  );
}
